import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import {
  MessstelleQrService
} from '../../../../Services/messstelle-qr.service';

import {
  MessstelleQrScannerDialogComponent
} from './messstelle-qr-scanner-dialog/messstelle-qr-scanner-dialog.component';

import {
  AKTIVITAETS_GRUPPEN,
  AktivitaetsGruppe,
  AktivitaetAnzeige,
  aktivitaetIcon,
  aktivitaetTitelVon,
  aktivitaetsGruppeVon,
  formatAktivitaet,
  MessstelleDetail,
  MessstelleBild,
  MessstelleKategorie,
  MessstellePruefpunkt,
  MessstellePruefprotokoll,
  PruefpunktSection,
  PruefpunktErgebnis,
  Pruefergebnis,
  SignalStatus,
  PRUEFPUNKT_ERGEBNIS_OPTIONEN,
  PRUEFERGEBNIS_OPTIONEN,
  SIGNAL_STATUS_OPTIONEN,
  normalizePruefpunktErgebnis,
  normalizePruefergebnis,
  normalizeSignalStatus,
  messstelleFeldMitAliassen,
  istLokaleId,
  PruefpunktId,
  ProtokollId,
  Anlagenteil,
  FELD_KENNZEICHNUNG,
  kennzeichnungLabel,
  standardFelder,
  ListenSpalte,
  SpaltenDatentyp,
  strukturSpalten,
  pruefeSpaltenWert,
  datumAlsIso,
  isoAlsDatum
} from '../../../../Models/messstelle.model';

import { Datenquelle } from '../../../../Models/offline.model';

import { InbetriebnahmeService } from '../../../../Services/inbetriebnahme.service';
import { InbetriebnahmeDataService } from '../../../../Services/offline/inbetriebnahme-data.service';
import {
  MessstelleQrDialogComponent
} from '../messstelle-qr-dialog/messstelle-qr-dialog.component';


interface KopfdatenEintrag {
  label: string;

  /** Anzeigetext, inklusive Platzhalter für fehlende Werte. */
  value: string;

  /**
   * Standardfelder hinter diesem Eintrag. Ein Feld = eine einfache Angabe,
   * zwei Felder = eine Spanne, die getrennt bearbeitet wird.
   * Leer bei Einträgen, die nur angezeigt und nicht bearbeitet werden.
   */
  felder: string[];

  /** Beschriftung je Eingabefeld im Bearbeitungsmodus. */
  eingabeLabels: string[];

  /** Arbeitskopie der Rohwerte während der Bearbeitung. */
  eingaben: string[];

  /**
   * Nur bei manuell erstellten Listen: Datentyp der Spalte (steuert die
   * Eingabe), Pflicht, Kennzeichnung und ob der Eintrag im kompakten Teil steht.
   * `felder` trägt dann die stabile Spalten-Id statt eines Standardfelds.
   */
  typ?: SpaltenDatentyp;
  pflicht?: boolean;
  kennzeichnung?: boolean;
  kompakt?: boolean;
}


@Component({
  selector: 'app-messstelle-detail',
  templateUrl: './messstelle-detail.component.html',
  styleUrls: ['./messstelle-detail.component.css']
})
export class MessstelleDetailComponent implements OnInit, OnDestroy {

  messstelle: MessstelleDetail | null = null;

  kategorien: MessstelleKategorie[] = [];

  kopfdaten: KopfdatenEintrag[] = [];

  /** Alle relevanten Excel-Spalten, die nicht schon als Kernangabe oben stehen. */
  weitereDaten: KopfdatenEintrag[] = [];

  /**
   * Die allgemeinen Daten starten bewusst kompakt. Sichtbar bleiben nur
   * Kennzeichnung und Langtext; alles Weitere kann bei Bedarf aufgeklappt werden.
   */
  weitereAllgemeineDatenOffen = false;

  sections: PruefpunktSection[] = [];


  // Kategorie
  selectedKategorieId: number | null = null;

  savingKategorie = false;

  // Anlagenteil
  //
  // Nur eine Gruppierungsinformation für Suche, Filter und spätere
  // Auswertungen. Die Zuordnung ist optional und hat keine Auswirkung auf
  // Prüfpunkte oder Ergebnisse.
  //
  // Bewusst online-only: es gibt dafür keinen Offline-Änderungstyp, und
  // eine stillschweigend verworfene Änderung wäre schlimmer als eine
  // ausgegraute Auswahl.
  selectedAnlagenteilId: number | null = null;

  anlagenteile: Anlagenteil[] = [];

  savingAnlagenteil = false;

  /** Formular für einen neuen Anlagenteil. Nur sichtbar, wenn ausgeklappt. */
  neuerAnlagenteilOffen = false;

  neuerAnlagenteilName = '';


  // Allgemeine Daten bearbeiten
  bearbeiten = false;

  savingWerte = false;


  // Seite
  isLoading = true;

  loadError = '';


  // Fortschritt
  gesamt = 0;

  /** Noch nicht geprüft – der einzige Zustand, der als offen zählt. */
  offen = 0;

  bestanden = 0;

  nichtBestanden = 0;

  irrelevant = 0;

  /** Bestanden + NichtBestanden + Irrelevant. */
  abgearbeitet = 0;

  fortschrittProzent = 0;


  // Prüfung der Messstelle
  selectedSignalStatus: SignalStatus = SignalStatus.Offen;

  selectedPruefergebnis: Pruefergebnis = Pruefergebnis.NichtGeprueft;

  /**
   * Arbeitskopie der allgemeinen Notiz zur Messstelle.
   *
   * Freiwillig und unabhängig vom Prüfergebnis: sie blockiert weder das
   * Speichern noch das Umstellen des Ergebnisses. Gespeichert wird sie in
   * `pruefergebnisBemerkung` – der gewachsene Name des Feldes im Backend.
   */
  messstelleNotiz = '';

  /** Zuletzt gespeicherter Stand – erspart überflüssige Anfragen beim Verlassen des Feldes. */
  private messstelleNotizGespeichert = '';

  savingSignalStatus = false;

  savingPruefergebnis = false;

  savingNotiz = false;

  schnellPruefungLaeuft = false;


  // Prüfverlauf
  pruefprotokoll: MessstellePruefprotokoll[] = [];


  // Neuer Prüfpunkt
  neuerPunktText = '';

  neuerPunktSection = '';

  addingPunkt = false;


  // Enums und Auswahllisten für das Template
  readonly Ergebnis = PruefpunktErgebnis;
  readonly Gesamtergebnis = Pruefergebnis;
  readonly Signal = SignalStatus;

  readonly ergebnisOptionen = PRUEFPUNKT_ERGEBNIS_OPTIONEN;
  readonly pruefergebnisOptionen = PRUEFERGEBNIS_OPTIONEN;
  readonly signalStatusOptionen = SIGNAL_STATUS_OPTIONEN;


  // aktuell speichernde Prüfpunkte
  private savingPunkte = new Set<PruefpunktId>();


  // zuletzt gespeicherte Bemerkung
  private bemerkungGespeichert = new Map<PruefpunktId, string>();


  /**
   * Zuletzt vom Server bestätigtes Ergebnis je Prüfpunkt.
   *
   * Nötig, weil ein "nicht bestanden" ohne Fehlerbeschreibung in der
   * Oberfläche schon steht, aber noch nicht gespeichert ist – daran erkennt
   * das Speichern der Beschreibung, dass es beides senden muss.
   */
  private ergebnisGespeichert = new Map<PruefpunktId, PruefpunktErgebnis>();


  /**
   * Woher die angezeigten Daten stammen. Die Seite muss das NICHT abfragen,
   * um zu funktionieren – der Datendienst entscheidet das. Angezeigt wird es
   * trotzdem: wer offline arbeitet, soll sehen, dass er es tut.
   */
  quelle: Datenquelle = 'server';

  /** Stand der lokalen Daten, wenn offline gearbeitet wird. */
  standUtc: string | null = null;

  hatAktivenQr =
    false;


  qrStatusLoading =
    false;


  constructor(
    private data: InbetriebnahmeDataService,
    private service: InbetriebnahmeService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private qrService:
      MessstelleQrService
  ) { }


  /** Wird gerade aus dem lokalen Bestand gearbeitet? */
  get istOffline(): boolean {
    return this.quelle === 'offline';
  }


  /** Hat dieser Prüfpunkt eine noch nicht synchronisierte Änderung? */
  istNichtSynchronisiert(punkt: MessstellePruefpunkt): boolean {
    return punkt.nichtSynchronisiert === true || istLokaleId(punkt.id);
  }


  // ========================================================
  // INIT
  // ========================================================

  ngOnInit(): void {

    const id = Number(
      this.route.snapshot.paramMap.get('id')
    );


    if (!id) {

      this.loadError =
        'Ungültige Messstellen-Id.';

      this.isLoading = false;

      return;
    }


    this.starteSucheEntprellung();

    this.loadKategorien();

    this.loadMessstelle(id);
  }


  // ========================================================
  // LADEN
  // ========================================================

  private loadKategorien(): void {

    this.data.getKategorien().subscribe({

      next: ergebnis => {

        this.kategorien =
          ergebnis.daten ?? [];

      },

      error: () => {

        this.kategorien = [];

      }

    });
  }


  private loadMessstelle(
    id: number
  ): void {

    this.isLoading = true;

    this.loadError = '';


    this.data.getMessstelle(id).subscribe({

      next: ergebnis => {

        this.quelle = ergebnis.quelle;

        this.standUtc = ergebnis.standUtc ?? null;

        this.apply(ergebnis.daten);

        this.isLoading = false;

        // QR-Status nach dem Laden der Messstelle prüfen
        this.ladeQrStatus();

      },

      error: err => {

        console.error(
          'Messstelle konnte nicht geladen werden:',
          err
        );

        /*
         * Der Datendienst hat es bereits über das Netz UND aus dem lokalen
         * Bestand versucht. Kommt hier ein Fehler an, gibt es die Messstelle
         * schlicht nirgends – der Hinweis nennt deshalb den wahrscheinlichen
         * Grund statt nur "fehlgeschlagen".
         */
        this.loadError =
          err?.message
          ?? 'Die Messstelle konnte nicht geladen werden.';

        this.isLoading = false;

      }

    });
  }

  openQrCode(): void {

    if (
      !this.messstelle
      ||
      !this.canEdit
    ) {
      return;
    }

    if (this.istOffline) {

      this.snackBar.open(
        'Der QR-Code kann nur mit Serververbindung erstellt oder geladen werden.',
        'OK',
        {
          duration: 4000
        }
      );

      return;
    }

    this.dialog.open(
      MessstelleQrDialogComponent,
      {
        width: '520px',
        maxWidth: '95vw',

        data: {

          messstelleId:
            this.messstelle.id,

          kennzeichnungLabel:
            this.kennzeichnungAnzeige,

          kennzeichnung:
            this.getStandardFieldValue(
              FELD_KENNZEICHNUNG
            ),

          langtext:
            this.getStandardFieldValue(
              'Langtext'
            ),

          projektName:
            this.messstelle.projektName,

          projektNummer:
            this.messstelle.projektNummer
        }
      }
    );
  }


  /**
   * Meldung nach einem Schreibvorgang.
   *
   * Wurde offline gespeichert, ist das KEIN Fehler: die Prüfung liegt sicher
   * im Gerät und geht bei der nächsten Verbindung hinaus. "Speichern
   * fehlgeschlagen" wäre an dieser Stelle schlicht gelogen und würde den
   * Monteur dazu bringen, dieselbe Prüfung noch einmal zu erfassen.
   */
  private meldeGespeichert(lokal: boolean, online: string): void {

    this.snackBar.open(
      lokal
        ? 'Lokal gespeichert – wird bei Verbindung synchronisiert.'
        : online,
      'OK',
      {
        duration: lokal ? 3500 : 2500
      }
    );
  }


  // ========================================================
  // KOMPLETTE MESSSTELLE ÜBERNEHMEN
  // ========================================================

  private apply(detail: MessstelleDetail): void {

    detail.pruefpunkte =
      detail.pruefpunkte ?? [];

    detail.values =
      detail.values ?? {};

    detail.columns =
      detail.columns ?? [];


    /*
     * Das Backend serialisiert Enums je nach Aufruf als Zahl oder als Name.
     * Hier einmal auf den Zahlwert bringen, damit die Vergleiche im Template
     * verlässlich sind.
     */
    for (const punkt of detail.pruefpunkte) {
      punkt.ergebnis = normalizePruefpunktErgebnis(punkt.ergebnis);
    }

    detail.pruefergebnis =
      normalizePruefergebnis(detail.pruefergebnis);

    detail.signalStatus =
      normalizeSignalStatus(detail.signalStatus);


    this.messstelle = detail;

    this.selectedKategorieId =
      detail.kategorieId ?? null;

    this.selectedAnlagenteilId =
      detail.anlagenteilId ?? null;

    this.anlagenteile =
      detail.anlagenteile ?? [];

    this.selectedPruefergebnis =
      detail.pruefergebnis;

    this.selectedSignalStatus =
      detail.signalStatus;

    this.messstelleNotiz =
      detail.pruefergebnisBemerkung ?? '';

    this.messstelleNotizGespeichert =
      this.messstelleNotiz;

    this.pruefprotokoll =
      detail.pruefprotokoll ?? [];

    /*
     * Erst aus dem mitgelieferten Verlauf zeichnen – so steht sofort etwas
     * da. Den gefilterten Ausschnitt holt `ladeVerlauf()` gleich danach;
     * offline bleibt es bei dieser lokalen Sicht.
     */
    this.wendeLokalenFilterAn();

    this.ladeVerlauf();


    this.buildKopfdaten();


    this.bemerkungGespeichert.clear();

    this.ergebnisGespeichert.clear();

    for (const punkt of detail.pruefpunkte) {

      this.bemerkungGespeichert.set(
        punkt.id,
        punkt.bemerkung ?? ''
      );

      this.ergebnisGespeichert.set(
        punkt.id,
        punkt.ergebnis
      );
    }


    this.buildSections();

    this.applyBilder(detail.bilder);
  }


  // ========================================================
  // ALLGEMEINE DATEN
  // ========================================================

  /**
   * Die allgemeinen Informationen einer Messstelle sind FEST.
   *
   * Sie werden immer und vollständig angezeigt, unabhängig davon, wie die
   * Excel-Datei aufgebaut ist. Die Strukturerkennung entscheidet nur, aus
   * welcher Excel-Spalte der jeweilige Wert kommt – nicht, ob das Feld
   * überhaupt erscheint. Fehlt ein Wert, steht dort LEER.
   */
  private static readonly LEER = '-----';


  /** Für das Template, das die Konstante nicht direkt lesen kann. */
  get leerPlatzhalter(): string {
    return MessstelleDetailComponent.LEER;
  }


  /**
   * Der kompakte, immer sichtbare Teil der Stammdaten.
   * Die konkrete Beschriftung der Kennzeichnung kommt bereits aus
   * `kennzeichnungAnzeige` und lautet dadurch z.B. KKS, AKZ oder SKS.
   */
  get kopfdatenKompakt(): KopfdatenEintrag[] {

    return this.kopfdaten.filter(eintrag => this.istKompakt(eintrag));
  }


  /** Alle übrigen Standardfelder, die erst nach dem Aufklappen erscheinen. */
  get kopfdatenDetails(): KopfdatenEintrag[] {

    return this.kopfdaten.filter(eintrag => !this.istKompakt(eintrag));
  }


  /**
   * Kompakt sichtbar: Kennzeichnung und Langtext. Bei manuell erstellten
   * Listen entscheidet die Struktur (Kennzeichnungsspalte und die Spalte,
   * die dem Langtext zugeordnet ist).
   */
  private istKompakt(eintrag: KopfdatenEintrag): boolean {

    if (eintrag.kompakt != null) {
      return eintrag.kompakt;
    }

    return eintrag.felder.includes(FELD_KENNZEICHNUNG)
      || eintrag.felder.includes('Langtext');
  }


  /** Spalten der Struktur, wenn die Liste manuell erstellt wurde; sonst leer. */
  get strukturSpalten(): ListenSpalte[] {
    return strukturSpalten(this.messstelle?.struktur);
  }


  get istManuelleListe(): boolean {
    return this.strukturSpalten.length > 0;
  }


  /** Gibt es hinter der kompakten Ansicht überhaupt weitere Informationen? */
  get hatWeitereAllgemeineDaten(): boolean {

    return (
      this.kopfdatenDetails.length > 0
      || this.weitereDaten.length > 0
    );
  }


  private buildKopfdaten(): void {

    this.kopfdaten = [];

    this.weitereDaten = [];


    if (!this.messstelle) {
      return;
    }


    /*
     * Jede Excel-Spalte, die hier oben schon dargestellt wird, darf
     * unter "Weitere relevante Daten" nicht noch einmal auftauchen.
     */
    const verbraucht = new Set<string>();


    /*
     * Welche Felder eine Messstelle hat, hängt am Typ ihrer Liste. Eine
     * Verbraucherliste beschreibt kein Signal, sondern ein elektrisches
     * Betriebsmittel – dort wären Messbereich und Signalart durchgehend
     * leer und Nennleistung und Motortyp würden fehlen.
     *
     * Altbestände ohne gespeicherten Typ gelten als Messstellenliste und
     * sehen deshalb exakt aus wie vor dem Umbau.
     */
    this.kopfdaten =
      this.istManuelleListe
        ? this.strukturKopfdaten(verbraucht)
        : this.messstelle.listenTyp === 'Verbraucherliste'
          ? this.verbraucherKopfdaten(verbraucht)
          : this.messstellenKopfdaten(verbraucht);


    this.buildWeitereDaten(verbraucht);
  }


  /**
   * Manuell erstellte Liste: das Formular folgt der Spaltenstruktur – jede
   * Spalte ein Eintrag, in der festgelegten Reihenfolge, die Kennzeichnung
   * vorne. Bearbeitet wird über die stabile Spalten-Id; der Datentyp steuert
   * die Eingabe (Zahl, Datum, Ja/Nein), Pflichtfelder sind markiert.
   */
  private strukturKopfdaten(
    verbraucht: Set<string>
  ): KopfdatenEintrag[] {

    const values = this.messstelle?.values ?? {};
    const mapping = this.messstelle?.fieldMapping ?? {};
    const langtextSpalte = mapping['Langtext'] ?? null;

    return this.strukturSpalten.map(spalte => {

      verbraucht.add(spalte.name);

      const wert = this.trimmed(values[spalte.name]);

      return {
        label: spalte.name + (spalte.pflicht ? ' *' : ''),
        value: wert || MessstelleDetailComponent.LEER,
        felder: [spalte.id],
        eingabeLabels: [spalte.name],
        // Datumsfelder werden im Browser-Format (JJJJ-MM-TT) bearbeitet.
        eingaben: [spalte.datentyp === 'Datum' ? datumAlsIso(wert) : wert],
        typ: spalte.datentyp,
        pflicht: spalte.pflicht,
        kennzeichnung: spalte.istKennzeichnung,
        kompakt: spalte.istKennzeichnung || spalte.name === langtextSpalte
      };
    });
  }


  /** Die feste Reihenfolge einer Messstellenliste. */
  private messstellenKopfdaten(
    verbraucht: Set<string>
  ): KopfdatenEintrag[] {

    return [
      this.einfachesFeld(this.kennzeichnungAnzeige, FELD_KENNZEICHNUNG, verbraucht),
      this.einfachesFeld('Langtext', 'Langtext', verbraucht),
      this.einfachesFeld('Kurztext', 'Kurztext', verbraucht),
      this.einfachesFeld('P&ID Schema', 'P&ID Schema', verbraucht),
      this.einfachesFeld('Medium', 'Medium', verbraucht),
      this.einfachesFeld('Signal', 'Signal', verbraucht),
      this.einfachesFeld('Signalart', 'Signalart', verbraucht),
      this.einfachesFeld('Hilfsspannung', 'Hilfsspannung', verbraucht),
      this.einfachesFeld('Funktion', 'Funktion', verbraucht),

      this.messbereichFeld(verbraucht),

      this.spannenFeld(
        'Warnung Tief-Hoch', 'Warnung Tief', 'Warnung Hoch', verbraucht),

      this.spannenFeld(
        'Alarm Tief-Hoch', 'Alarm Tief', 'Alarm Hoch', verbraucht),

      this.einfachesFeld('Einheit', 'Messbereich Einheit', verbraucht),
      this.einfachesFeld('Kalibrierung', 'Kalibrierung', verbraucht),
      this.einfachesFeld('Sensor Hersteller', 'Sensor Hersteller', verbraucht),
      this.einfachesFeld('Sensor Typ', 'Sensor Typ', verbraucht)
    ];
  }


  /**
   * Die feste Reihenfolge einer Verbraucherliste.
   *
   * Entsteht aus demselben Katalog, den auch Import und Server verwenden –
   * eine zweite Liste hier wäre eine Fehlerquelle. Nur die Kennzeichnung
   * bekommt ihre eigene Beschriftung ("SKS" statt "Kennzeichnung").
   */
  private verbraucherKopfdaten(
    verbraucht: Set<string>
  ): KopfdatenEintrag[] {

    return standardFelder('Verbraucherliste').map(feld =>
      this.einfachesFeld(
        feld === FELD_KENNZEICHNUNG ? this.kennzeichnungAnzeige : feld,
        feld,
        verbraucht));
  }


  /**
   * Wie die Kennzeichnung dieser Liste heißt: "KKS", "SKS", "AKZ" … oder
   * neutral "Kennzeichnung", solange die Art nicht feststeht.
   *
   * Der Server liefert die Beschriftung fertig; der lokale Rückfall greift
   * nur bei einem älteren Offline-Bestand, der sie noch nicht kennt.
   */
  get kennzeichnungAnzeige(): string {
    return this.messstelle?.kennzeichnungLabel
      || kennzeichnungLabel(this.messstelle?.kennzeichnungsArt);
  }


  /** Ein Feld, ein Wert, eine Eingabe. */
  private einfachesFeld(
    label: string,
    feld: string,
    verbraucht: Set<string>
  ): KopfdatenEintrag {

    const wert = this.getStandardFieldValue(feld, verbraucht);

    return {
      label,
      value: wert || MessstelleDetailComponent.LEER,
      felder: [feld],
      eingabeLabels: [label],
      eingaben: [wert]
    };
  }


  /**
   * Zwei zusammengehörige Grenzwerte als ein Feld: "10 – 80".
   * Fehlt eine Seite, steht dort der Platzhalter: "10 – -----".
   * Bearbeitet wird weiterhin getrennt nach von und bis.
   */
  private spannenFeld(
    label: string,
    vonFeld: string,
    bisFeld: string,
    verbraucht: Set<string>
  ): KopfdatenEintrag {

    const leer = MessstelleDetailComponent.LEER;

    const von = this.getStandardFieldValue(vonFeld, verbraucht);
    const bis = this.getStandardFieldValue(bisFeld, verbraucht);

    return {
      label,
      value: (!von && !bis) ? leer : `${von || leer} – ${bis || leer}`,
      felder: [vonFeld, bisFeld],
      eingabeLabels: ['von', 'bis'],
      eingaben: [von, bis]
    };
  }


  /**
   * Der Messbereich wird als ein Feld dargestellt.
   *
   * Führt die Datei bereits eine kombinierte Spalte ("0 - 16 bar"), wird
   * genau diese angezeigt und bearbeitet. Sonst entsteht der Wert aus Min
   * und Max, die getrennt bearbeitet werden. Die Einheit steht bewusst
   * NICHT hier, sie hat ein eigenes Feld.
   */
  private messbereichFeld(
    verbraucht: Set<string>
  ): KopfdatenEintrag {

    const label = 'Messbereich von-bis';

    const leer = MessstelleDetailComponent.LEER;

    const kombiniert =
      this.getStandardFieldValue('Messbereich', verbraucht);

    const min =
      this.getStandardFieldValue('Messbereich Min', verbraucht);

    const max =
      this.getStandardFieldValue('Messbereich Max', verbraucht);


    /*
     * Die kombinierte Spalte gewinnt, sobald die Datei sie führt – auch
     * wenn sie für diese Zeile leer ist. Sonst würde eine Eingabe in ein
     * anderes Feld wandern als das, aus dem die Anzeige liest.
     */
    if (kombiniert || (this.hatZuordnung('Messbereich') && !min && !max)) {
      return {
        label,
        value: kombiniert || leer,
        felder: ['Messbereich'],
        eingabeLabels: [label],
        eingaben: [kombiniert]
      };
    }


    return {
      label,
      value: (!min && !max) ? leer : `${min || leer} – ${max || leer}`,
      felder: ['Messbereich Min', 'Messbereich Max'],
      eingabeLabels: ['von', 'bis'],
      eingaben: [min, max]
    };
  }


  /**
   * Wert eines Standardfelds über die beim Import gespeicherte Zuordnung.
   *
   * Gesucht wird zuerst unter dem aktuellen Feldnamen und danach unter den
   * früher verwendeten Namen. Dadurch funktionieren Listen, die vor der
   * Umbenennung importiert wurden, ohne Datenmigration weiter.
   *
   * Jede berührte Excel-Spalte wird in <c>verbraucht</c> vermerkt – auch
   * wenn sie leer ist. Sonst würde eine zugeordnete, aber leere Spalte
   * später unter "Weitere relevante Daten" wieder auftauchen.
   */
  private getStandardFieldValue(
    standardField: string,
    verbraucht?: Set<string>
  ): string {

    if (!this.messstelle) {
      return '';
    }

    const mapping = this.messstelle.fieldMapping ?? {};
    const values = this.messstelle.values ?? {};

    let gefunden = '';

    for (const feld of messstelleFeldMitAliassen(standardField)) {

      // KI-Mapping: z.B. "Sensor Hersteller" -> "Sensor / Manufacturer"
      const gemappteSpalte = mapping[feld];

      if (gemappteSpalte) {

        verbraucht?.add(gemappteSpalte);

        if (!gefunden) {
          gefunden = this.trimmed(values[gemappteSpalte]);
        }
      }

      // Fallback: die Excel-Spalte heißt bereits exakt so.
      if (!gefunden) {

        const direkt = this.trimmed(values[feld]);

        if (direkt) {
          verbraucht?.add(feld);
          gefunden = direkt;
        }
      }
    }

    return gefunden;
  }


  /**
   * Hat die Liste für dieses Standardfeld überhaupt eine Spalte – auch wenn
   * sie für diese Messstelle leer ist? Spiegelt die Suche im Backend.
   */
  private hatZuordnung(
    standardField: string
  ): boolean {

    if (!this.messstelle) {
      return false;
    }

    const mapping = this.messstelle.fieldMapping ?? {};
    const columns = this.messstelle.columns ?? [];

    return messstelleFeldMitAliassen(standardField).some(
      feld => !!mapping[feld] || columns.includes(feld)
    );
  }


  // ========================================================
  // ALLGEMEINE DATEN BEARBEITEN
  // ========================================================

  /**
   * Die Anzeige ist reiner Text. Erst der Bearbeiten-Knopf tauscht sie gegen
   * Eingabefelder – je Eintrag eines, bei Spannen zwei (von und bis).
   */
  starteBearbeitung(): void {

    if (!this.canEdit || this.savingWerte) {
      return;
    }

    // Von vorne mit den aktuellen Werten beginnen.
    this.buildKopfdaten();

    /*
     * Beim Bearbeiten alle Felder sichtbar machen. So kann kein Feld unbemerkt
     * verborgen bleiben, während der Bearbeitungsmodus aktiv ist.
     */
    this.weitereAllgemeineDatenOffen = true;

    this.bearbeiten = true;
  }


  brichBearbeitungAb(): void {

    if (this.savingWerte) {
      return;
    }

    this.bearbeiten = false;

    // Verworfene Eingaben wegwerfen.
    this.buildKopfdaten();
  }


  /**
   * Schickt alle bearbeitbaren Felder als Standardfeld -> Wert zum Server.
   *
   * Bewusst alle und nicht nur die geänderten: der Server vergleicht selbst
   * und legt eine Spalte nur an, wenn wirklich ein Wert ankommt. Die Antwort
   * ist die frisch gespeicherte Messstelle, damit Anzeige und Datenbank
   * garantiert übereinstimmen.
   */
  speichereWerte(): void {

    if (!this.messstelle || !this.canEdit || this.savingWerte) {
      return;
    }


    const werte: { [standardFeld: string]: string } = {};

    for (const eintrag of this.kopfdaten) {

      eintrag.felder.forEach((feld, i) => {
        const roh = this.trimmed(eintrag.eingaben[i]);

        // Datumsfelder manueller Listen gehen in der gespeicherten Form TT.MM.JJJJ hinaus.
        werte[feld] = eintrag.typ === 'Datum' ? isoAlsDatum(roh) : roh;
      });
    }


    /*
     * Manuell erstellte Liste: Datentypen und Pflichtfelder vor dem Senden
     * prüfen – dieselben Regeln wie serverseitig, nur ohne Netzumweg. Die
     * Eindeutigkeit der Kennzeichnung prüft allein der Server.
     */
    if (this.istManuelleListe) {

      const fehler: string[] = [];

      for (const spalte of this.strukturSpalten) {
        const wert = werte[spalte.id] ?? '';

        if (spalte.pflicht && !wert) {
          fehler.push(spalte.istKennzeichnung
            ? `Die Kennzeichnung „${spalte.name}“ ist ein Pflichtfeld.`
            : `„${spalte.name}“ ist ein Pflichtfeld.`);
          continue;
        }

        const typFehler = pruefeSpaltenWert(spalte, wert);

        if (typFehler) {
          fehler.push(typFehler);
        }
      }

      if (fehler.length > 0) {
        this.snackBar.open(fehler.join(' '), 'OK', { duration: 5000 });
        return;
      }
    }


    this.savingWerte = true;


    this.data.updateWerte(
      this.messstelle,
      werte
    ).subscribe({

      next: ergebnis => {

        this.apply(ergebnis.daten);

        this.bearbeiten = false;

        this.savingWerte = false;


        this.meldeGespeichert(
          ergebnis.lokal,
          'Allgemeine Daten gespeichert.'
        );

      },

      error: err => {

        console.error(
          'Allgemeine Daten konnten nicht gespeichert werden:',
          err
        );

        this.savingWerte = false;


        this.snackBar.open(
          err?.error?.message
          ?? 'Die allgemeinen Daten konnten nicht gespeichert werden.',
          'OK',
          {
            duration: 4000
          }
        );

      }

    });
  }


  // ========================================================
  // WEITERE RELEVANTE DATEN
  // ========================================================

  /**
   * Alle übrigen Excel-Spalten mit Inhalt, die oben noch nicht stehen –
   * in der Reihenfolge der Datei.
   *
   * Seit dem Umbau der Spaltenauswahl werden nur noch die beim Import
   * ANGEHAKTEN Spalten gespeichert – hier steht also ohnehin nur, was
   * jemand haben wollte.
   *
   * Die IGNORE-Prüfung unten gilt ALTEN Listen: dort hiess IGNORE
   * „importiert, aber nachrangig“, und solche Spalten stehen noch in den
   * Werten. Sie bleiben hier ausgeblendet, damit sich an diesen Listen
   * nichts ändert. Ältere Listen ohne gespeicherte Klassifikation zeigen
   * wie bisher alle übrigen Spalten.
   */
  private buildWeitereDaten(
    verbraucht: Set<string>
  ): void {

    this.weitereDaten = [];


    if (!this.messstelle) {
      return;
    }


    const values = this.messstelle.values ?? {};

    const klassifikationen =
      this.messstelle.columnClassifications ?? {};


    /*
     * Reihenfolge der Datei bevorzugen, danach alles, was nur in den
     * Werten steht (z.B. wenn sich die Spaltenliste geändert hat).
     */
    const reihenfolge = [
      ...(this.messstelle.columns ?? []),
      ...Object.keys(values)
    ];


    const gesehen = new Set<string>();


    for (const spalte of reihenfolge) {

      if (!spalte || gesehen.has(spalte)) {
        continue;
      }

      gesehen.add(spalte);


      // Steht bereits unter "Allgemeine Daten".
      if (verbraucht.has(spalte)) {
        continue;
      }


      // Nachrangige Verwaltungsinformation – bleibt in der Tabelle sichtbar.
      if (klassifikationen[spalte] === 'IGNORE') {
        continue;
      }


      const value = this.trimmed(values[spalte]);

      if (!value) {
        continue;
      }


      this.weitereDaten.push({
        label: spalte,
        value,
        felder: [],
        eingabeLabels: [],
        eingaben: []
      });
    }
  }


  private trimmed(
    value: string | null | undefined
  ): string {

    return (value ?? '').trim();
  }



  // ========================================================
  // ANLAGENTEIL
  // ========================================================

  /**
   * Setzt den Anlagenteil oder hebt die Zuordnung auf.
   *
   * Anders als die Kategorie geht das ausschließlich online: es gibt dafür
   * keinen Offline-Änderungstyp, und eine Änderung, die beim Synchronisieren
   * still verlorenginge, wäre schlimmer als eine ausgegraute Auswahl.
   */
  onAnlagenteilChange(
    anlagenteilId: number | null
  ): void {

    if (
      this.savingAnlagenteil ||
      !this.messstelle ||
      !this.canEdit ||
      this.istOffline
    ) {

      return;
    }


    const neu = anlagenteilId ?? null;

    const aktuell = this.messstelle.anlagenteilId ?? null;


    if (neu === aktuell) {
      return;
    }


    this.savingAnlagenteil = true;


    this.service
      .setAnlagenteil(
        this.messstelle.id,
        neu
      )
      .subscribe({

        next: detail => {

          this.apply(detail);

          this.savingAnlagenteil = false;

          this.ladeQrStatus();


          this.snackBar.open(

            neu === null
              ? 'Zuordnung zum Anlagenteil entfernt.'
              : 'Anlagenteil gespeichert.',

            'OK',

            {
              duration: 3000
            }

          );

        },


        error: err => {

          this.selectedAnlagenteilId =
            this.messstelle?.anlagenteilId
            ?? null;


          this.savingAnlagenteil = false;


          this.snackBar.open(

            err?.error?.message
            ?? 'Der Anlagenteil konnte nicht gespeichert werden.',

            'OK',

            {
              duration: 4000
            }

          );

        }

      });
  }


  /**
   * Legt einen neuen Anlagenteil an und ordnet die Messstelle sofort zu.
   *
   * Vor Ort fällt oft erst auf, dass eine Gruppe fehlt. Den Umweg über den
   * Importdialog zu verlangen wäre die falsche Reihenfolge.
   */
  neuenAnlagenteilAnlegen(): void {

    const name = this.neuerAnlagenteilName.trim();

    if (
      !name ||
      !this.messstelle ||
      !this.canEdit ||
      this.istOffline ||
      this.savingAnlagenteil
    ) {

      return;
    }


    this.savingAnlagenteil = true;


    this.service
      .createAnlagenteil(
        this.messstelle.messstellenListeId,
        name
      )
      .subscribe({

        next: anlagenteil => {

          this.anlagenteile = [
            ...this.anlagenteile.filter(a => a.id !== anlagenteil.id),
            anlagenteil
          ].sort((a, b) => a.name.localeCompare(b.name, 'de'));


          this.neuerAnlagenteilName = '';
          this.neuerAnlagenteilOffen = false;
          this.savingAnlagenteil = false;

          this.onAnlagenteilChange(anlagenteil.id);

        },


        error: err => {

          this.savingAnlagenteil = false;


          this.snackBar.open(

            err?.error?.message
            ?? 'Der Anlagenteil konnte nicht angelegt werden.',

            'OK',

            {
              duration: 4000
            }

          );

        }

      });
  }


  // ========================================================
  // KATEGORIE
  // ========================================================

  onKategorieChange(
    kategorieId: number | null
  ): void {

    if (
      this.savingKategorie ||
      !this.messstelle ||
      !this.canEdit
    ) {

      return;
    }


    const neu =
      kategorieId ?? null;


    const aktuell =
      this.messstelle.kategorieId ?? null;


    if (neu === aktuell) {

      return;
    }


    this.savingKategorie = true;


    this.data
      .setKategorie(
        this.messstelle,
        neu
      )
      .subscribe({

        next: ergebnis => {

          this.apply(ergebnis.daten);

          this.savingKategorie = false;


          this.meldeGespeichert(
            ergebnis.lokal,

            neu === null
              ? 'Kategorie entfernt.'
              : 'Kategorie gespeichert.'
          );

        },


        error: err => {

          console.error(
            'Kategorie konnte nicht gespeichert werden:',
            err
          );


          this.selectedKategorieId =
            this.messstelle?.kategorieId
            ?? null;


          this.savingKategorie = false;


          this.snackBar.open(

            err?.error?.message
            ?? err?.message
            ?? 'Die Kategorie konnte nicht gespeichert werden.',

            'OK',

            {
              duration: 4000
            }

          );

        }

      });
  }


  // ========================================================
  // PRÜFPUNKTE GRUPPIEREN
  // ========================================================

  private buildSections(): void {

    const punkte =
      this.messstelle?.pruefpunkte
      ?? [];


    const gruppen =
      new Map<
        string,
        MessstellePruefpunkt[]
      >();


    const sortiert =
      [...punkte].sort(
        (a, b) => {

          const sortOrder =
            (a.sortOrder ?? 0)
            -
            (b.sortOrder ?? 0);


          if (sortOrder !== 0) {

            return sortOrder;

          }


          /*
           * Bei gleichem SortOrder entscheidet die Kennung. Ein offline
           * angelegter Punkt trägt statt einer Zahl eine Client-UUID –
           * deshalb Text- statt Zahlenvergleich. Solche Punkte sortieren
           * dadurch ans Ende ihres Abschnitts, was passt: sie sind die
           * zuletzt ergänzten.
           */
          return String(a.id).localeCompare(String(b.id), 'de', {
            numeric: true
          });
        }
      );


    for (const punkt of sortiert) {

      const name =
        punkt.section?.trim()
        ||
        'Weitere Prüfpunkte';


      const gruppe =
        gruppen.get(name);


      if (gruppe) {

        gruppe.push(punkt);

      } else {

        gruppen.set(
          name,
          [punkt]
        );

      }
    }


    this.sections =
      Array.from(
        gruppen.entries()
      ).map(
        ([name, punkte]) => ({

          name,

          punkte,

          gesamt:
            punkte.length,

          erledigt:
            punkte.filter(
              p =>
                normalizePruefpunktErgebnis(p.ergebnis)
                !== PruefpunktErgebnis.NichtGeprueft
            ).length

        })
      );


    this.recomputeCounts();
  }


  // ========================================================
  // FORTSCHRITT
  // ========================================================

  /**
   * Fortschritt und Zählwerte neu bestimmen.
   *
   * Als abgearbeitet gelten Bestanden, NichtBestanden UND Irrelevant –
   * offen ist allein "nicht geprüft". Der Fortschritt sagt damit nur, wie
   * viele Prüfungen durchgeführt wurden; ob die Messstelle fachlich
   * bestanden hat, steht getrennt im Gesamtergebnis.
   */
  private recomputeCounts(): void {

    const punkte =
      this.messstelle?.pruefpunkte ?? [];


    const zaehle = (ergebnis: PruefpunktErgebnis): number =>
      punkte.filter(
        p => normalizePruefpunktErgebnis(p.ergebnis) === ergebnis
      ).length;


    this.gesamt =
      punkte.length;

    this.offen =
      zaehle(PruefpunktErgebnis.NichtGeprueft);

    this.bestanden =
      zaehle(PruefpunktErgebnis.Bestanden);

    this.nichtBestanden =
      zaehle(PruefpunktErgebnis.NichtBestanden);

    this.irrelevant =
      zaehle(PruefpunktErgebnis.Irrelevant);

    this.abgearbeitet =
      this.gesamt - this.offen;


    this.fortschrittProzent =
      this.gesamt > 0
        ? Math.round(
          (this.abgearbeitet / this.gesamt) * 100
        )
        : 0;


    for (const section of this.sections) {

      section.gesamt =
        section.punkte.length;


      section.erledigt =
        section.punkte.filter(
          p =>
            normalizePruefpunktErgebnis(p.ergebnis)
            !== PruefpunktErgebnis.NichtGeprueft
        ).length;
    }




  }

  private pruefeAutomatischenAbschluss(): void {

    if (
      !this.messstelle ||
      !this.canEdit
    ) {
      return;
    }

    const punkte =
      this.messstelle.pruefpunkte ?? [];

    if (punkte.length === 0) {
      return;
    }


    const ergebnisse =
      punkte.map(
        p =>
          normalizePruefpunktErgebnis(
            p.ergebnis
          )
      );


    const hatOffene =
      ergebnisse.some(
        e =>
          e === PruefpunktErgebnis.NichtGeprueft
      );


    const hatNichtBestanden =
      ergebnisse.some(
        e =>
          e === PruefpunktErgebnis.NichtBestanden
      );


    // =====================================================
    // NOCH NICHT ALLES GEPRÜFT
    // =====================================================

    if (hatOffene) {

      /*
       * Wurde eine bereits fertige Messstelle wieder geöffnet,
       * ist sie wieder in Bearbeitung.
       */
      if (
        normalizeSignalStatus(
          this.messstelle.signalStatus
        ) !== SignalStatus.InBearbeitung
      ) {
        this.onSignalStatusChange(
          SignalStatus.InBearbeitung
        );
      }


      /*
       * Sobald wieder mindestens ein Prüfpunkt offen ist,
       * darf die Messstelle nicht mehr Bestanden /
       * Nicht bestanden sein.
       */
      if (
        normalizePruefergebnis(
          this.messstelle.pruefergebnis
        ) !== Pruefergebnis.NichtGeprueft
      ) {
        this.onPruefergebnisChange(
          Pruefergebnis.NichtGeprueft
        );
      }

      return;
    }


    // =====================================================
    // ALLE PRÜFPUNKTE SIND GEPRÜFT
    // =====================================================

    if (
      normalizeSignalStatus(
        this.messstelle.signalStatus
      ) !== SignalStatus.Getestet
    ) {
      this.onSignalStatusChange(
        SignalStatus.Getestet
      );
    }


    // Mindestens ein Fehler -> gesamte Messstelle nicht bestanden.
    const zielErgebnis =
      hatNichtBestanden
        ? Pruefergebnis.NichtBestanden
        : Pruefergebnis.Bestanden;


    if (
      normalizePruefergebnis(
        this.messstelle.pruefergebnis
      ) !== zielErgebnis
    ) {
      this.onPruefergebnisChange(
        zielErgebnis
      );
    }
  }

  // ========================================================
  // PRÜFERGEBNIS EINES PRÜFPUNKTS
  // ========================================================

  /** Aktuelles Ergebnis eines Punktes als Zahlwert. */
  ergebnisVon(
    punkt: MessstellePruefpunkt
  ): PruefpunktErgebnis {

    return normalizePruefpunktErgebnis(punkt.ergebnis);
  }


  /** Ist bei diesem Punkt eine Fehlerbeschreibung Pflicht? */
  brauchtFehlerbeschreibung(
    punkt: MessstellePruefpunkt
  ): boolean {

    return (
      this.ergebnisVon(punkt)
      === PruefpunktErgebnis.NichtBestanden
    );
  }


  /** Beschriftung des Bemerkungsfeldes – je nach Ergebnis Pflicht oder optional. */
  bemerkungLabel(
    punkt: MessstellePruefpunkt
  ): string {

    return this.brauchtFehlerbeschreibung(punkt)
      ? 'Fehlerbeschreibung *'
      : 'Bemerkung (optional)';
  }


  /** Fehlt bei einem nicht bestandenen Punkt die Beschreibung? */
  fehlerbeschreibungFehlt(
    punkt: MessstellePruefpunkt
  ): boolean {

    return (
      this.brauchtFehlerbeschreibung(punkt)
      &&
      !(punkt.bemerkung ?? '').trim()
    );
  }


  /**
   * Setzt das Prüfergebnis eines Punktes.
   *
   * "Nicht bestanden" verlangt eine Fehlerbeschreibung. Steht noch keine da,
   * wird das Ergebnis in der Oberfläche schon einmal umgestellt, aber NICHT
   * gespeichert – der Server würde es ohnehin ablehnen. Gespeichert wird,
   * sobald die Beschreibung eingetragen und das Feld verlassen wird.
   */
  setErgebnis(
    punkt: MessstellePruefpunkt,
    ergebnis: PruefpunktErgebnis
  ): void {

    if (
      !this.canEdit ||
      this.savingKategorie ||
      this.isSaving(punkt.id)
    ) {
      return;
    }


    ergebnis = normalizePruefpunktErgebnis(ergebnis);


    if (this.ergebnisVon(punkt) === ergebnis) {
      return;
    }


    const altesErgebnis =
      this.ergebnisVon(punkt);


    // UI sofort aktualisieren, danach speichern.
    punkt.ergebnis = ergebnis;

    this.recomputeCounts();


    if (
      ergebnis === PruefpunktErgebnis.NichtBestanden
      &&
      !(punkt.bemerkung ?? '').trim()
    ) {

      this.snackBar.open(
        'Bitte eine Fehlerbeschreibung eintragen – erst dann wird das Ergebnis gespeichert.',
        'OK',
        {
          duration: 5000
        }
      );

      return;
    }


    this.speicherePunkt(
      punkt,
      ergebnis,
      punkt.bemerkung ?? null,
      () => {

        punkt.ergebnis = altesErgebnis;

        this.recomputeCounts();
      },
      'Das Prüfergebnis konnte nicht gespeichert werden.'
    );
  }

  schnellPruefung(): void {

    if (
      !this.messstelle ||
      !this.canEdit ||
      this.schnellPruefungLaeuft
    ) {
      return;
    }

    const punkte =
      this.messstelle.pruefpunkte ?? [];


    if (punkte.length === 0) {
      return;
    }


    /*
     * Punkte, die bereits bestanden sind,
     * müssen nicht noch einmal gespeichert werden.
     */
    const zuPruefen =
      punkte.filter(
        p =>
          normalizePruefpunktErgebnis(
            p.ergebnis
          ) !== PruefpunktErgebnis.Bestanden
      );


    /*
     * Alles war ohnehin schon bestanden.
     * Trotzdem Gesamtstatus prüfen.
     */
    if (zuPruefen.length === 0) {

      this.recomputeCounts();
      this.pruefeAutomatischenAbschluss();

      return;
    }


    this.schnellPruefungLaeuft = true;

    this.schnellPruefungNaechsterPunkt(
      zuPruefen,
      0
    );
  }


  private schnellPruefungNaechsterPunkt(
    punkte: MessstellePruefpunkt[],
    index: number
  ): void {

    if (!this.messstelle) {

      this.schnellPruefungLaeuft = false;

      return;
    }


    /*
     * Alle Punkte erfolgreich gespeichert.
     */
    if (index >= punkte.length) {

      this.schnellPruefungLaeuft = false;

      this.recomputeCounts();

      /*
       * Dadurch werden anschließend automatisch:
       *
       * Signalstatus → Getestet
       * Prüfergebnis → Bestanden
       */
      this.pruefeAutomatischenAbschluss();


      this.snackBar.open(
        'Alle Prüfpunkte wurden auf „Bestanden“ gesetzt.',
        'OK',
        {
          duration: 3000
        }
      );

      return;
    }


    const punkt =
      punkte[index];


    this.savingPunkte.add(
      punkt.id
    );


    this.data.updatePruefpunkt(
      this.messstelle,
      punkt,
      PruefpunktErgebnis.Bestanden,
      punkt.bemerkung ?? null
    ).subscribe({

      next: ergebnis => {

        this.applyPunkt(
          punkt,
          ergebnis.daten
        );


        this.savingPunkte.delete(
          punkt.id
        );


        /*
         * Nächsten Prüfpunkt erst speichern,
         * wenn dieser fertig ist.
         */
        this.schnellPruefungNaechsterPunkt(
          punkte,
          index + 1
        );
      },


      error: err => {

        this.savingPunkte.delete(
          punkt.id
        );

        this.schnellPruefungLaeuft = false;

        this.recomputeCounts();


        this.snackBar.open(
          err?.error?.message
          ?? `Der Prüfpunkt „${punkt.bezeichnung}“ konnte nicht gespeichert werden.`,
          'OK',
          {
            duration: 5000
          }
        );
      }

    });
  }


  // ========================================================
  // BEMERKUNG / FEHLERBESCHREIBUNG
  // ========================================================

  saveBemerkung(
    punkt: MessstellePruefpunkt
  ): void {

    if (
      !this.canEdit ||
      this.savingKategorie ||
      this.isSaving(punkt.id)
    ) {

      return;
    }


    const neu =
      (punkt.bemerkung ?? '')
        .trim();


    const alt =
      this.bemerkungGespeichert
        .get(punkt.id)
      ?? '';


    const ergebnis =
      this.ergebnisVon(punkt);


    /*
     * Ein noch nicht gespeichertes "nicht bestanden" wartet auf die
     * Fehlerbeschreibung. Sobald sie da ist, wird beides zusammen gespeichert –
     * auch wenn die Bemerkung selbst unverändert geblieben wäre.
     */
    const ergebnisOffen =
      ergebnis !== normalizePruefpunktErgebnis(
        this.ergebnisGespeichert.get(punkt.id)
      );


    if (neu === alt && !ergebnisOffen) {

      return;
    }


    if (
      ergebnis === PruefpunktErgebnis.NichtBestanden
      &&
      !neu
    ) {

      this.snackBar.open(
        'Bitte eine Fehlerbeschreibung angeben, wenn der Prüfpunkt nicht bestanden ist.',
        'OK',
        {
          duration: 4000
        }
      );

      return;
    }


    this.speicherePunkt(
      punkt,
      ergebnis,
      neu.length > 0 ? neu : null,
      () => {

        punkt.bemerkung = alt;
      },
      'Die Bemerkung konnte nicht gespeichert werden.'
    );
  }


  /**
   * Ein Prüfpunkt-Update an den Server. <paramref name="beiFehler"/> stellt
   * den vorherigen Stand wieder her, damit die Oberfläche nichts anzeigt, was
   * nicht gespeichert wurde.
   */
  private speicherePunkt(
    punkt: MessstellePruefpunkt,
    ergebnis: PruefpunktErgebnis,
    bemerkung: string | null,
    beiFehler: () => void,
    fehlertext: string
  ): void {

    if (!this.messstelle) {
      return;
    }


    this.savingPunkte.add(punkt.id);


    this.data.updatePruefpunkt(
      this.messstelle,
      punkt,
      ergebnis,
      bemerkung
    ).subscribe({

      next: ergebnisDaten => {

        this.applyPunkt(
          punkt,
          ergebnisDaten.daten
        );


        this.savingPunkte.delete(
          punkt.id
        );


        this.recomputeCounts();

        this.pruefeAutomatischenAbschluss();


        if (ergebnisDaten.lokal) {

          /*
           * Offline gespeichert. Der Verlauf steht bereits im lokalen
           * Bestand – ein Nachladen vom Server wäre zwecklos und würde den
           * eben erfassten Eintrag wieder verwerfen.
           */
          this.uebernehmeLokalenVerlauf(
            ergebnisDaten.pruefprotokoll
          );

          this.snackBar.open(
            'Lokal gespeichert – wird bei Verbindung synchronisiert.',
            'OK',
            {
              duration: 3000
            }
          );

          return;
        }


        // Der Verlauf hat sich um einen Eintrag ergänzt.
        this.ladePruefprotokoll();

      },


      error: err => {

        console.error(
          fehlertext,
          err
        );


        beiFehler();


        this.savingPunkte.delete(
          punkt.id
        );


        this.snackBar.open(
          err?.error?.message ?? fehlertext,
          'OK',
          {
            duration: 4000
          }
        );

      }

    });
  }


  // ========================================================
  // SIGNALSTATUS DER MESSSTELLE
  // ========================================================

  onSignalStatusChange(
    status: SignalStatus
  ): void {

    if (
      !this.messstelle ||
      !this.canEdit ||
      this.savingSignalStatus
    ) {
      return;
    }


    status = normalizeSignalStatus(status);


    const alterStatus =
      normalizeSignalStatus(this.messstelle.signalStatus);


    if (status === alterStatus) {
      return;
    }


    this.savingSignalStatus = true;

    this.selectedSignalStatus = status;


    this.data.setSignalStatus(
      this.messstelle,
      status
    ).subscribe({

      next: ergebnis => {

        this.apply(ergebnis.daten);

        this.savingSignalStatus = false;


        if (ergebnis.lokal) {

          this.meldeGespeichert(true, '');
        }

      },

      error: err => {

        console.error(
          'Signalstatus konnte nicht gespeichert werden:',
          err
        );


        this.selectedSignalStatus = alterStatus;

        this.savingSignalStatus = false;


        this.snackBar.open(
          err?.error?.message
          ?? 'Der Signalstatus konnte nicht gespeichert werden.',
          'OK',
          {
            duration: 4000
          }
        );

      }

    });
  }


  // ========================================================
  // GESAMTERGEBNIS DER MESSSTELLE
  // ========================================================

  /*
   * Zur Notiz zur Messstelle gibt es hier BEWUSST keine Pflichtlogik mehr.
   *
   * Sie war früher die Fehlerbeschreibung zum Gesamtergebnis und bei "nicht
   * bestanden" Pflicht. Heute ist sie eine freiwillige Notiz zur ganzen
   * Messstelle: sie hält das Ergebnis nicht auf, und ob eine Beschreibung
   * fachlich nötig ist, entscheidet allein der einzelne Prüfpunkt
   * (siehe `brauchtFehlerbeschreibung`).
   */

  /**
   * Gesamtergebnis umstellen. Wird immer sofort gespeichert – auch "nicht
   * bestanden".
   */
  onPruefergebnisChange(
    ergebnis: Pruefergebnis
  ): void {

    if (
      !this.messstelle ||
      !this.canEdit ||
      this.savingPruefergebnis
    ) {
      return;
    }


    this.selectedPruefergebnis =
      normalizePruefergebnis(ergebnis);


    this.speicherePruefergebnis();
  }


  /**
   * Die Notiz beim Verlassen des Feldes speichern, sofern sie sich geändert
   * hat.
   *
   * Ein eigener Schreibweg neben dem Prüfergebnis: die Notiz verändert das
   * Ergebnis nicht und wird von ihm nicht aufgehalten.
   */
  saveMessstelleNotiz(): void {

    if (
      !this.messstelle ||
      !this.canEdit ||
      this.savingNotiz
    ) {
      return;
    }


    const neu =
      this.messstelleNotiz.trim();


    if (neu === this.messstelleNotizGespeichert.trim()) {
      return;
    }


    this.savingNotiz = true;


    this.data.setMessstelleNotiz(
      this.messstelle,
      neu.length > 0 ? neu : null
    ).subscribe({

      next: ergebnis => {

        this.apply(ergebnis.daten);

        this.savingNotiz = false;


        if (ergebnis.lokal) {

          this.meldeGespeichert(true, '');
        }

      },

      error: err => {

        console.error(
          'Notiz konnte nicht gespeichert werden:',
          err
        );


        // Auf den zuletzt bestätigten Stand zurück.
        this.messstelleNotiz =
          this.messstelleNotizGespeichert;

        this.savingNotiz = false;


        this.snackBar.open(
          err?.error?.message
          ?? 'Die Notiz konnte nicht gespeichert werden.',
          'OK',
          {
            duration: 4000
          }
        );

      }

    });
  }


  private speicherePruefergebnis(): void {

    if (!this.messstelle) {
      return;
    }


    this.savingPruefergebnis = true;


    this.data.setPruefergebnis(
      this.messstelle,
      this.selectedPruefergebnis
    ).subscribe({

      next: ergebnis => {

        this.apply(ergebnis.daten);

        this.savingPruefergebnis = false;


        if (ergebnis.lokal) {

          this.meldeGespeichert(true, '');
        }

      },

      error: err => {

        console.error(
          'Prüfergebnis konnte nicht gespeichert werden:',
          err
        );


        // Auf den zuletzt bestätigten Stand zurück. Die Notiz bleibt, wie sie
        // ist: sie war an diesem Speichern nicht beteiligt.
        if (this.messstelle) {

          this.selectedPruefergebnis =
            normalizePruefergebnis(this.messstelle.pruefergebnis);
        }


        this.savingPruefergebnis = false;


        this.snackBar.open(
          err?.error?.message
          ?? 'Das Prüfergebnis konnte nicht gespeichert werden.',
          'OK',
          {
            duration: 4000
          }
        );

      }

    });
  }

  // ========================================================
  // AKTIVITÄTSVERLAUF
  //
  // Der vollständige Verlauf steht in `pruefprotokoll`: online kommt er mit
  // der Messstelle, offline aus dem lokalen Bestand. Angezeigt wird davon
  // `verlauf` – der gefilterte Ausschnitt.
  //
  // Gefiltert wird ONLINE serverseitig: eine lange bearbeitete Messstelle
  // sammelt hunderte Einträge, und die Suche über den vollen Text gehört in
  // die Datenbank. OFFLINE filtert dieselbe Logik lokal; das Ergebnis muss
  // dasselbe sein, sonst fände der Monteur ohne Netz etwas anderes.
  // ========================================================

  /** Wie viele Einträge auf einmal angezeigt werden. */
  private static readonly VERLAUF_SEITE = 25;

  /** Ab wie vielen Einträgen der Verlauf in sich scrollt. */
  private static readonly VERLAUF_SCROLL_AB = 5;


  /** Die Auswahl des Aktivitätsfilters, für das Template. */
  readonly aktivitaetsGruppen = AKTIVITAETS_GRUPPEN;

  /** Der angezeigte – gefilterte – Ausschnitt des Verlaufs. */
  verlauf: MessstellePruefprotokoll[] = [];

  /** Alle Personen, die an DIESER Messstelle Aktivitäten haben. */
  verlaufPersonen: string[] = [];

  /** Wie viele Einträge der aktuelle Filter insgesamt trifft. */
  verlaufGesamt = 0;

  verlaufHatMehr = false;

  verlaufLaedt = false;

  filterGruppe: AktivitaetsGruppe | null = null;
  filterPerson: string | null = null;
  filterVon: Date | null = null;
  filterBis: Date | null = null;
  filterSuche = '';


  /**
   * Die Suche wird nicht bei jedem Tastendruck abgeschickt.
   *
   * Ohne Verzögerung entstünde je Buchstabe eine Anfrage, und die Antworten
   * kämen in beliebiger Reihenfolge zurück – im schlechtesten Fall zeigte die
   * Liste am Ende das Ergebnis zu einem halb getippten Wort.
   */
  private readonly sucheEingabe = new Subject<string>();

  private sucheAbo?: Subscription;


  /** Ist irgendein Filter gesetzt? Steuert "Filter zurücksetzen". */
  get filterAktiv(): boolean {
    return (
      this.filterGruppe != null
      || !!this.filterPerson
      || !!this.filterVon
      || !!this.filterBis
      || !!this.filterSuche.trim()
    );
  }


  get verlaufScrollt(): boolean {
    return (
      this.verlauf.length
      >= MessstelleDetailComponent.VERLAUF_SCROLL_AB
    );
  }


  /**
   * Startet die Entprellung der Suche. Wird EINMAL in `ngOnInit` aufgerufen;
   * das Abonnement endet in `ngOnDestroy`.
   */
  private starteSucheEntprellung(): void {

    this.sucheAbo =
      this.sucheEingabe
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe(() => this.ladeVerlauf());
  }


  /** Aus dem Suchfeld. */
  onSucheGeaendert(): void {
    this.sucheEingabe.next(this.filterSuche.trim());
  }


  /** Aus den übrigen Filtern – die wirken sofort. */
  onFilterGeaendert(): void {
    this.ladeVerlauf();
  }


  filterZuruecksetzen(): void {

    this.filterGruppe = null;
    this.filterPerson = null;
    this.filterVon = null;
    this.filterBis = null;
    this.filterSuche = '';

    this.ladeVerlauf();
  }


  /**
   * Lädt den Verlauf neu.
   *
   * @param mehr `true` hängt die nächste Seite an, statt von vorne zu
   *             beginnen – für "Weitere anzeigen".
   */
  ladeVerlauf(mehr = false): void {

    const id = this.messstelle?.id;

    if (!id) {
      return;
    }


    if (this.istOffline) {

      /*
       * Ohne Verbindung gibt es nichts nachzuladen: der Verlauf steht
       * vollständig im lokalen Bestand – inklusive der soeben offline
       * erfassten Änderung, die der Server noch gar nicht kennt.
       */
      this.wendeLokalenFilterAn(mehr);

      return;
    }


    const bereits = mehr ? this.verlauf.length : 0;

    this.verlaufLaedt = true;


    this.service.getAktivitaeten(id, {
      gruppe: this.filterGruppe,
      person: this.filterPerson,
      vonUtc: this.grenzeAlsUtc(this.filterVon, false),
      bisUtc: this.grenzeAlsUtc(this.filterBis, true),
      suche: this.filterSuche.trim() || null,
      skip: bereits,
      take: MessstelleDetailComponent.VERLAUF_SEITE
    }).subscribe({

      next: ergebnis => {

        const neue = ergebnis?.eintraege ?? [];

        this.verlauf =
          mehr ? [...this.verlauf, ...neue] : neue;

        this.verlaufGesamt = ergebnis?.gesamt ?? this.verlauf.length;
        this.verlaufHatMehr = ergebnis?.hatMehr ?? false;
        this.verlaufPersonen = ergebnis?.personen ?? [];

        this.verlaufLaedt = false;
      },

      error: err => {

        /*
         * Der Verlauf ist Beiwerk – ein Fehler darf die Seite nicht stören.
         * Angezeigt wird dann, was ohnehin schon da ist: der Verlauf, den die
         * Messstelle mitgebracht hat.
         */
        console.error(
          'Aktivitätsverlauf konnte nicht geladen werden:',
          err
        );

        this.verlaufLaedt = false;

        if (!mehr) {
          this.wendeLokalenFilterAn(false);
        }
      }

    });
  }


  /**
   * Dieselbe Filterung wie serverseitig, nur über den lokalen Bestand.
   *
   * Der Weg ohne Netz. Käme hier etwas anderes heraus als online, hätte der
   * Monteur je nach Empfang eine andere Wahrheit vor sich.
   */
  private wendeLokalenFilterAn(mehr = false): void {

    const alle = this.pruefprotokoll ?? [];

    this.verlaufPersonen =
      Array.from(
        new Set(
          alle
            .map(e => (e.benutzerName ?? '').trim())
            .filter(name => !!name)
        )
      ).sort((a, b) => a.localeCompare(b, 'de'));


    const suche = this.filterSuche.trim().toLowerCase();

    const von = this.filterVon
      ? this.tagesBeginn(this.filterVon).getTime()
      : null;

    const bis = this.filterBis
      ? this.tagesEnde(this.filterBis).getTime()
      : null;


    const treffer = alle.filter(eintrag => {

      if (this.filterGruppe != null
        && aktivitaetsGruppeVon(eintrag.typ) !== this.filterGruppe) {
        return false;
      }

      if (this.filterPerson
        && (eintrag.benutzerName ?? '') !== this.filterPerson) {
        return false;
      }

      if (von != null || bis != null) {

        const zeit = new Date(eintrag.zeitpunktUtc).getTime();

        if (Number.isNaN(zeit)) {
          return false;
        }

        if (von != null && zeit < von) {
          return false;
        }

        if (bis != null && zeit > bis) {
          return false;
        }
      }

      return !suche || this.passtZurSuche(eintrag, suche);
    });


    const bisher =
      mehr
        ? this.verlauf.length
        : 0;

    const anzahl =
      bisher + MessstelleDetailComponent.VERLAUF_SEITE;

    this.verlauf = treffer.slice(0, anzahl);
    this.verlaufGesamt = treffer.length;
    this.verlaufHatMehr = treffer.length > this.verlauf.length;
  }


  /**
   * Trifft der Suchbegriff diesen Eintrag?
   *
   * Durchsucht werden Feldname, Prüfpunkt, alter und neuer Wert, Beschreibung,
   * Bemerkung und Person. Die beiden Textfelder decken dabei auch die
   * Enum-Einträge ab: „Bestanden" steht dort als fertiger Text, in der
   * Datenbank dagegen nur als Zahl.
   */
  private passtZurSuche(
    eintrag: MessstellePruefprotokoll,
    suche: string
  ): boolean {

    const felder = [
      eintrag.feldName,
      eintrag.pruefpunktBezeichnung,
      eintrag.altWert,
      eintrag.neuWert,
      eintrag.ergebnisText,
      eintrag.vorherigesErgebnisText,
      eintrag.beschreibung,
      eintrag.bemerkung,
      eintrag.benutzerName,
      aktivitaetTitelVon(eintrag)
    ];

    return felder.some(
      wert => !!wert && wert.toLowerCase().includes(suche)
    );
  }


  /**
   * Eine im Kalender gewählte Grenze als UTC-Zeitpunkt.
   *
   * Der Benutzer meint einen TAG in seiner Zeitzone, gespeichert sind
   * UTC-Zeitpunkte. Ohne diese Umrechnung fehlten je nach Zeitzone die
   * Einträge der frühen Morgen- bzw. späten Abendstunden.
   */
  private grenzeAlsUtc(
    tag: Date | null,
    ende: boolean
  ): string | null {

    if (!tag) {
      return null;
    }

    return (ende ? this.tagesEnde(tag) : this.tagesBeginn(tag))
      .toISOString();
  }


  private tagesBeginn(tag: Date): Date {
    const d = new Date(tag);
    d.setHours(0, 0, 0, 0);
    return d;
  }


  private tagesEnde(tag: Date): Date {
    const d = new Date(tag);
    d.setHours(23, 59, 59, 999);
    return d;
  }


  /**
   * Verlauf nachladen, nachdem ein Prüfpunkt gespeichert wurde. Bei den
   * Messstellen-Endpunkten ist das nicht nötig: die antworten bereits mit
   * der vollständigen Messstelle inklusive Verlauf.
   */
  private ladePruefprotokoll(): void {

    const id = this.messstelle?.id;

    if (!id) {
      return;
    }


    if (this.istOffline) {

      /*
       * Ohne Verbindung gibt es nichts nachzuladen – der Verlauf steht
       * bereits im lokalen Bestand und enthält auch die soeben offline
       * erfasste Änderung.
       */
      this.uebernehmeLokalenVerlauf();

      return;
    }


    /*
     * Den vollständigen Verlauf mitziehen: er ist zugleich der Vorrat, aus
     * dem die Anzeige bei einem Ausfall des gefilterten Endpunkts schöpft.
     */
    this.service.getPruefprotokoll(id).subscribe({

      next: eintraege => {

        this.pruefprotokoll = eintraege ?? [];

        if (this.messstelle) {
          this.messstelle.pruefprotokoll = this.pruefprotokoll;
        }

        this.ladeVerlauf();
      },

      error: err => {

        // Der Verlauf ist Beiwerk – ein Fehler darf die Seite nicht stören.
        console.error(
          'Aktivitätsverlauf konnte nicht geladen werden:',
          err
        );

      }

    });
  }


  /**
   * Übernimmt den Verlauf aus der bereits geladenen Messstelle.
   *
   * Nach einer offline erfassten Änderung steht der neue Eintrag schon im
   * lokalen Bestand. Ihn vom Server nachzuladen würde ihn im besten Fall
   * nicht finden und im schlechtesten die Anzeige zurücksetzen.
   */
  private uebernehmeLokalenVerlauf(
    protokoll?: MessstellePruefprotokoll[]
  ): void {

    this.pruefprotokoll =
      protokoll
      ?? this.messstelle?.pruefprotokoll
      ?? [];


    if (this.messstelle) {

      this.messstelle.pruefprotokoll =
        this.pruefprotokoll;
    }


    this.wendeLokalenFilterAn();
  }


  /**
   * Inhalt eines Verlaufseintrags – Titel, Betreff, Werte, Zusatz, Bemerkung.
   * Derselbe Formatter baut auch den Prüfverlauf im IBN-PDF.
   */
  protokollAnzeige(
    eintrag: MessstellePruefprotokoll
  ): AktivitaetAnzeige {

    return formatAktivitaet(eintrag);
  }


  /** Icon je Aktivitätsart – hält den Verlauf auch beim Überfliegen lesbar. */
  protokollIcon(
    eintrag: MessstellePruefprotokoll
  ): string {

    return aktivitaetIcon(eintrag.typ);
  }


  /** CSS-Klasse für die farbliche Einordnung eines Eintrags. */
  protokollGruppeCss(
    eintrag: MessstellePruefprotokoll
  ): string {

    return `gruppe-${aktivitaetsGruppeVon(eintrag.typ)}`;
  }


  trackByProtokoll(
    _index: number,
    eintrag: MessstellePruefprotokoll
  ): ProtokollId {

    return eintrag.id;
  }



  // ========================================================
  // PRÜFPUNKT HINZUFÜGEN
  // ========================================================

  addPunkt(): void {

    if (
      !this.canEdit ||
      this.savingKategorie ||
      this.addingPunkt ||
      !this.messstelle
    ) {

      return;
    }


    const bezeichnung =
      this.neuerPunktText.trim();


    if (!bezeichnung) {

      return;
    }


    const section =
      this.neuerPunktSection
        .trim();


    this.addingPunkt = true;


    this.data.createPruefpunkt(

      this.messstelle,

      bezeichnung,

      section || null

    ).subscribe({

      next: ergebnis => {

        const punkt = ergebnis.daten;

        if (!this.messstelle) {

          this.addingPunkt = false;

          return;
        }


        this.messstelle
          .pruefpunkte
          .push(punkt);


        punkt.ergebnis =
          normalizePruefpunktErgebnis(punkt.ergebnis);


        if (ergebnis.lokal) {

          this.meldeGespeichert(true, '');
        }


        this.bemerkungGespeichert.set(

          punkt.id,

          punkt.bemerkung ?? ''

        );


        this.ergebnisGespeichert.set(

          punkt.id,

          punkt.ergebnis

        );


        this.neuerPunktText = '';

        this.neuerPunktSection = '';


        this.buildSections();


        this.addingPunkt = false;


        // Das Anlegen steht jetzt im Verlauf – offline lokal, online beim
        // Server. `ladePruefprotokoll` trifft beide Fälle.
        if (ergebnis.lokal) {
          this.uebernehmeLokalenVerlauf(ergebnis.pruefprotokoll);
        } else {
          this.ladePruefprotokoll();
        }

      },


      error: err => {

        console.error(
          'Prüfpunkt konnte nicht angelegt werden:',
          err
        );


        this.addingPunkt = false;


        this.snackBar.open(

          'Der Prüfpunkt konnte nicht angelegt werden.',

          'OK',

          {
            duration: 4000
          }

        );

      }

    });
  }


  // ========================================================
  // PRÜFPUNKT LÖSCHEN
  // ========================================================

  deletePunkt(
    punkt: MessstellePruefpunkt
  ): void {

    if (
      !this.canEdit ||
      this.savingKategorie ||
      this.isSaving(punkt.id) ||
      !this.messstelle
    ) {

      return;
    }


    if (
      !confirm(
        `Prüfpunkt "${punkt.bezeichnung}" wirklich entfernen?`
      )
    ) {

      return;
    }


    this.savingPunkte.add(
      punkt.id
    );


    if (!this.messstelle) {
      return;
    }


    this.data.deletePruefpunkt(
      this.messstelle,
      punkt
    ).subscribe({

      next: ergebnis => {

        if (!this.messstelle) {

          return;
        }


        if (ergebnis.lokal) {

          this.meldeGespeichert(true, '');
        }


        this.messstelle.pruefpunkte =

          this.messstelle.pruefpunkte.filter(

            p =>
              p.id !== punkt.id

          );


        this.savingPunkte.delete(
          punkt.id
        );


        this.ergebnisGespeichert.delete(
          punkt.id
        );


        this.bemerkungGespeichert.delete(
          punkt.id
        );


        this.buildSections();


        // Das Löschen steht jetzt im Verlauf – der Eintrag überlebt den
        // Prüfpunkt.
        if (ergebnis.lokal) {
          this.uebernehmeLokalenVerlauf(ergebnis.pruefprotokoll);
        } else {
          this.ladePruefprotokoll();
        }

      },


      error: err => {

        console.error(
          'Prüfpunkt konnte nicht gelöscht werden:',
          err
        );


        this.savingPunkte.delete(
          punkt.id
        );


        this.snackBar.open(

          'Der Prüfpunkt konnte nicht gelöscht werden.',

          'OK',

          {
            duration: 4000
          }

        );

      }

    });
  }


  // ========================================================
  // EINEN PRÜFPUNKT AKTUALISIEREN
  // ========================================================

  private applyPunkt(
    ziel: MessstellePruefpunkt,
    quelle: MessstellePruefpunkt
  ): void {

    ziel.ergebnis =
      normalizePruefpunktErgebnis(quelle.ergebnis);

    ziel.status =
      quelle.status;

    ziel.bewertung =
      quelle.bewertung;

    ziel.bemerkung =
      quelle.bemerkung;

    ziel.quittiertAmUtc =
      quelle.quittiertAmUtc;

    ziel.quittiertVon =
      quelle.quittiertVon;

    /*
     * Den Änderungsstand mitziehen. Ohne ihn ginge die nächste Änderung mit
     * einer veralteten Basis los, und der Server meldete zu Recht einen
     * Konflikt – obwohl derselbe Benutzer der einzige war, der geschrieben hat.
     */
    ziel.version =
      quelle.version ?? ziel.version;

    ziel.nichtSynchronisiert =
      quelle.nichtSynchronisiert === true;


    this.ergebnisGespeichert.set(
      ziel.id,
      ziel.ergebnis
    );


    this.bemerkungGespeichert.set(
      ziel.id,
      quelle.bemerkung ?? ''
    );
  }


  // ========================================================
  // TEMPLATE-HILFEN
  // ========================================================

  get canEdit(): boolean {

    return (
      this.messstelle?.canEdit
      === true
    );
  }


  /** Zum Projekt der Messstelle springen, sofern eines zugeordnet ist. */
  oeffneProjekt(): void {

    const projektId =
      this.messstelle?.projektId;


    if (projektId) {

      this.router.navigate(
        ['/projekte', projektId]
      );
    }
  }


  isSaving(
    punktId: PruefpunktId
  ): boolean {

    return this.savingPunkte.has(
      punktId
    );
  }


  isManuell(
    punkt: MessstellePruefpunkt
  ): boolean {

    return (
      punkt.vorlageId == null
    );
  }


  trackBySection(
    _index: number,
    section: PruefpunktSection
  ): string {

    return section.name;
  }


  trackByPunkt(
    _index: number,
    punkt: MessstellePruefpunkt
  ): PruefpunktId {

    return punkt.id;
  }


  trackByKopf(
    _index: number,
    kopf: KopfdatenEintrag
  ): string {

    return kopf.label;
  }

  // ========================================================
  // BILDER
  // ========================================================

  /**
   * Object-URLs je Bild-Id.
   *
   * Die Bilder liegen hinter einem geschützten Endpunkt; ein `<img src>`
   * schickt den Bearer-Token nicht mit. Sie werden deshalb als Blob geladen
   * und als Object-URL angezeigt. Beim Verlassen der Seite und beim Löschen
   * werden sie wieder freigegeben, sonst hält der Browser den Speicher.
   */
  bildUrls = new Map<number, string>();

  /** Bilder, deren Blob gerade geladen wird – für den Platzhalter. */
  private ladendeBilder = new Set<number>();

  uploadLaeuft = false;

  uploadFehler = '';

  /** Bild-Id, deren Beschreibung gerade gespeichert wird. */
  private speicherndeBilder = new Set<number>();

  /** Zuletzt gespeicherte Beschreibung je Bild – erspart leere Speicheraufrufe. */
  private beschreibungGespeichert = new Map<number, string>();


  /** Übernimmt die Bilderliste und lädt fehlende Vorschauen nach. */
  private applyBilder(bilder: MessstelleBild[] | null | undefined): void {

    const aktuelle = bilder ?? [];

    if (this.messstelle) {
      this.messstelle.bilder = aktuelle;
    }


    const vorhandeneIds = new Set(aktuelle.map(b => b.id));


    // Nicht mehr vorhandene Vorschauen freigeben.
    for (const [id, url] of Array.from(this.bildUrls.entries())) {
      if (!vorhandeneIds.has(id)) {
        URL.revokeObjectURL(url);
        this.bildUrls.delete(id);
      }
    }


    for (const bild of aktuelle) {

      this.beschreibungGespeichert.set(
        bild.id,
        bild.beschreibung ?? ''
      );

      this.ladeBildVorschau(bild.id);
    }
  }


  private ladeBildVorschau(bildId: number): void {

    if (this.bildUrls.has(bildId) || this.ladendeBilder.has(bildId)) {
      return;
    }

    this.ladendeBilder.add(bildId);

    this.service.getBildBlob(bildId).subscribe({

      next: blob => {
        this.ladendeBilder.delete(bildId);
        this.bildUrls.set(bildId, URL.createObjectURL(blob));
      },

      error: err => {
        this.ladendeBilder.delete(bildId);
        console.error('Bild konnte nicht geladen werden:', err);
      }

    });
  }


  bildUrl(bild: MessstelleBild): string | null {
    return this.bildUrls.get(bild.id) ?? null;
  }


  istBildSpeichernd(bildId: number): boolean {
    return this.speicherndeBilder.has(bildId);
  }


  /**
   * Auswahl aus Kamera, Galerie oder Explorer – für den Browser ist das
   * dasselbe File-Input, nur mit anderem `capture`-Attribut.
   *
   * Mehrfachauswahl wird nacheinander hochgeladen, damit ein Fehler bei
   * einer Datei die übrigen nicht mitreisst.
   */
  async onBildAusgewaehlt(event: Event): Promise<void> {

    const input = event.target as HTMLInputElement;

    const originale = input.files
      ? Array.from(input.files)
      : [];

    if (
      originale.length === 0 ||
      !this.messstelle ||
      !this.canEdit
    ) {
      return;
    }

    this.uploadFehler = '';
    this.uploadLaeuft = true;

    try {

      const dateien: File[] = [];

      for (const original of originale) {

        if (original.size <= 0) {
          throw new Error(
            `"${original.name}" enthält keine Bilddaten.`
          );
        }

        /*
         * Wichtig für iOS:
         * Die Kameradatei sofort vollständig in den Speicher lesen.
         * Danach sind wir nicht mehr von der temporären Datei
         * des File-Inputs abhängig.
         */
        const buffer =
          await original.arrayBuffer();


        let contentType =
          original.type;

        if (!contentType) {

          const name =
            original.name.toLowerCase();

          if (
            name.endsWith('.jpg') ||
            name.endsWith('.jpeg')
          ) {
            contentType = 'image/jpeg';
          }
          else if (name.endsWith('.png')) {
            contentType = 'image/png';
          }
          else if (name.endsWith('.heic')) {
            contentType = 'image/heic';
          }
          else if (name.endsWith('.heif')) {
            contentType = 'image/heif';
          }
          else if (name.endsWith('.webp')) {
            contentType = 'image/webp';
          }
          else {
            contentType = 'application/octet-stream';
          }
        }


        const sichereDatei =
          new File(
            [buffer],
            original.name || `image-${Date.now()}.jpg`,
            {
              type: contentType,
              lastModified:
                original.lastModified || Date.now()
            }
          );


        console.log(
          'Bild für Upload vorbereitet:',
          {
            originalSize: original.size,
            neueSize: sichereDatei.size,
            name: sichereDatei.name,
            type: sichereDatei.type
          }
        );


        dateien.push(
          sichereDatei
        );
      }


      /*
       * Erst NACHDEM die Bytes kopiert wurden,
       * darf der File-Input geleert werden.
       */
      input.value = '';


      this.ladeNaechstesBildHoch(
        dateien,
        0
      );

    }
    catch (err: any) {

      input.value = '';

      this.uploadLaeuft = false;

      console.error(
        'Bild konnte nicht vorbereitet werden:',
        err
      );

      this.uploadFehler =
        err?.message
        ?? 'Das ausgewählte Bild konnte nicht gelesen werden.';
    }
  }

  private ladeNaechstesBildHoch(
    dateien: File[],
    index: number
  ): void {

    if (
      !this.messstelle ||
      index >= dateien.length
    ) {
      this.uploadLaeuft = false;
      return;
    }

    const datei =
      dateien[index];

    console.log(
      'Bild wird hochgeladen:',
      {
        name: datei.name,
        type: datei.type,
        size: datei.size
      }
    );


    this.service.uploadBild(
      this.messstelle.id,
      datei,
      null
    ).subscribe({

      next: bild => {

        if (this.messstelle) {

          this.messstelle.bilder = [
            bild,
            ...(this.messstelle.bilder ?? [])
          ];

          this.beschreibungGespeichert.set(
            bild.id,
            bild.beschreibung ?? ''
          );

          this.ladeBildVorschau(
            bild.id
          );
        }


        this.ladeNaechstesBildHoch(
          dateien,
          index + 1
        );
      },


      error: err => {

        console.error(
          'Bild konnte nicht hochgeladen werden:',
          {
            status: err?.status,
            response: err?.error,
            name: datei.name,
            type: datei.type,
            size: datei.size
          }
        );

        this.uploadLaeuft = false;

        this.uploadFehler =
          err?.error?.message
          ?? `"${datei.name}" konnte nicht hochgeladen werden. Fehler ${err?.status ?? 'unbekannt'}.`;
      }
    });
  }


  /** Beschreibung beim Verlassen des Feldes speichern. */
  saveBildBeschreibung(bild: MessstelleBild): void {

    if (!this.canEdit || this.istBildSpeichernd(bild.id)) {
      return;
    }

    const neu = this.trimmed(bild.beschreibung);
    const alt = this.beschreibungGespeichert.get(bild.id) ?? '';

    if (neu === alt) {
      return;
    }

    this.speicherndeBilder.add(bild.id);

    this.service.updateBildBeschreibung(
      bild.id,
      neu.length > 0 ? neu : null
    ).subscribe({

      next: aktualisiert => {

        bild.beschreibung = aktualisiert.beschreibung;

        this.beschreibungGespeichert.set(
          bild.id,
          aktualisiert.beschreibung ?? ''
        );

        this.speicherndeBilder.delete(bild.id);

        this.ladePruefprotokoll();
      },

      error: err => {

        console.error('Beschreibung konnte nicht gespeichert werden:', err);

        this.speicherndeBilder.delete(bild.id);

        this.snackBar.open(
          'Die Beschreibung konnte nicht gespeichert werden.',
          'OK',
          { duration: 4000 }
        );
      }

    });
  }


  deleteBild(bild: MessstelleBild): void {

    if (!this.canEdit || this.istBildSpeichernd(bild.id)) {
      return;
    }

    if (!confirm('Dieses Bild wirklich löschen?')) {
      return;
    }

    this.speicherndeBilder.add(bild.id);

    this.service.deleteBild(bild.id).subscribe({

      next: () => {

        if (this.messstelle) {
          this.messstelle.bilder =
            (this.messstelle.bilder ?? []).filter(b => b.id !== bild.id);
        }

        const url = this.bildUrls.get(bild.id);

        if (url) {
          URL.revokeObjectURL(url);
          this.bildUrls.delete(bild.id);
        }

        this.beschreibungGespeichert.delete(bild.id);

        this.speicherndeBilder.delete(bild.id);

        this.ladePruefprotokoll();
      },

      error: err => {

        console.error('Bild konnte nicht gelöscht werden:', err);

        this.speicherndeBilder.delete(bild.id);

        this.snackBar.open(
          'Das Bild konnte nicht gelöscht werden.',
          'OK',
          { duration: 4000 }
        );
      }

    });
  }


  /** Grössenangabe für die Bildunterschrift. */
  bildGroesse(bild: MessstelleBild): string {

    const kb = bild.sizeBytes / 1024;

    return kb < 1024
      ? `${Math.round(kb)} KB`
      : `${(kb / 1024).toFixed(1)} MB`;
  }



  /**
   * Bild in voller Grösse in einem neuen Tab öffnen.
   *
   * Die Object-URL gehört zu diesem Dokument – der neue Tab kann sie lesen,
   * solange diese Seite offen bleibt. Das reicht für "kurz genauer ansehen".
   */
  oeffneBild(bild: MessstelleBild): void {

    const url = this.bildUrls.get(bild.id);

    if (url) {
      window.open(url, '_blank');
    }
  }

  trackByBild(_index: number, bild: MessstelleBild): number {
    return bild.id;
  }


  // ========================================================
  // AUFRÄUMEN
  // ========================================================

  ngOnDestroy(): void {

    // Object-URLs der Bildvorschauen freigeben.
    for (const url of this.bildUrls.values()) {
      URL.revokeObjectURL(url);
    }

    this.bildUrls.clear();

    // Die entprellte Suche würde sonst nach dem Verlassen der Seite
    // weiterlaufen und auf einer Komponente arbeiten, die es nicht mehr gibt.
    this.sucheAbo?.unsubscribe();
  }


  // ========================================================
  // ZURÜCK
  // ========================================================

  /**
   * Projekt, aus dem heraus geöffnet wurde – durchgereicht von der
   * Messstellenliste. Leer beim Weg über Wartung → Inbetriebnahme.
   */
  private get herkunftParameter(): { [key: string]: number } {

    const roh =
      this.route
        .snapshot
        .queryParamMap
        .get('vonProjekt');

    const id = Number(roh);

    return roh && !Number.isNaN(id) && id > 0
      ? { vonProjekt: id }
      : {};
  }


  /**
   * Zurück zur Messstellenliste – und zwar so, dass ein weiteres „Zurück"
   * wieder im Projekt landet, falls der Weg von dort kam. Die Herkunft
   * wandert deshalb als Query-Parameter mit.
   */
  back(): void {

    const herkunft =
      this.herkunftParameter;


    if (this.messstelle) {

      this.router.navigate(
        [
          '/wartung/inbetriebnahme/liste',

          this.messstelle
            .messstellenListeId
        ],

        { queryParams: herkunft }
      );

    } else {

      this.router.navigate([

        '/wartung/inbetriebnahme'

      ]);

    }
  }

  private ladeQrStatus(): void {

    if (!this.messstelle) {
      return;
    }

    this.qrStatusLoading = true;

    this.qrService
      .getQrStatus(
        this.messstelle.id
      )
      .subscribe({

        next: status => {

          this.hatAktivenQr =
            status.hatAktivenQr;

          this.qrStatusLoading =
            false;
        },

        error: err => {

          console.error(
            'QR-Status konnte nicht geladen werden:',
            err
          );

          /*
           * Bei einem echten Fehler nicht so tun,
           * als gäbe es keinen QR-Code.
           */
          this.qrStatusLoading =
            false;
        }

      });
  }

  qrCodeVergeben(): void {

    if (
      !this.messstelle
      ||
      this.qrStatusLoading
    ) {
      return;
    }


    const ersetzen =
      this.hatAktivenQr;


    if (ersetzen) {

      const bestaetigt =
        window.confirm(
          'Der aktuell vergebene QR-Code wird deaktiviert und durch den neu gescannten QR-Code ersetzt.\n\nFortfahren?'
        );

      if (!bestaetigt) {
        return;
      }
    }


    const dialogRef =
      this.dialog.open(
        MessstelleQrScannerDialogComponent,
        {

          width:
            '520px',

          maxWidth:
            '96vw',

          disableClose:
            false,

          data: {

            messstelleId:
              this.messstelle.id,

            ersetzen:
              ersetzen
          }
        }
      );


    dialogRef
      .afterClosed()
      .subscribe(result => {

        if (!result) {
          return;
        }

        /*
         * Ab jetzt besitzt diese Messstelle
         * definitiv einen aktiven QR-Code.
         */
        this.hatAktivenQr = true;

        // Zustand nochmals direkt vom Server bestätigen
        this.ladeQrStatus();

        this.snackBar.open(
          result.ersetzt
            ? 'Der QR-Code wurde erfolgreich neu zugewiesen.'
            : 'Der QR-Code wurde erfolgreich zugewiesen.',
          'OK',
          {
            duration: 3500
          }
        );

      });
  }
}